import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { cardPortionAud } from '@/domain/settlementFlow';
import type { DocketLine, SalePayment, SettlementLine, SettlementLump, StudioId } from '@/domain/types';
import { dayKey } from '@/utils/studioDay';

/**
 * The SIMULATED acquirer (W7 n1, DECIDED 24 Sep). Real bank + EFTPOS integrations
 * stay simulated; this module stands in for both so a live checkout walks the
 * whole three-way trail (diagram 11): make-sale record → terminal docket → bank
 * settlement line.
 *
 *  - `echoTerminalDocket(sale)` runs AT the sale: the terminal prints a docket
 *    for the card portion only (a mixed sale's cash stays in the till).
 *  - `echoSettlementBatch(studio, day)` runs AT DAY CLOSE: the bank batches the
 *    day's unsettled terminal dockets into one lump that lands the next business
 *    day. Idempotent — re-closing a day never duplicates or rewrites it.
 *  - `landPendingBatches(studio)` is the console fast-forward: on the frozen demo
 *    clock the next business day never arrives, so it echoes the live checkouts'
 *    batch and lands it now.
 *
 * Provider-agnostic, like the rest of settlement: no card brand appears. Seeded
 * dockets and lumps are never touched — sim records carry their own id prefixes.
 */

const SIM_DOCKET_PREFIX = 'docket-sim-';
const SIM_LUMP_PREFIX = 'lump-sim-';

/** The neutral terminal id a studio's simulated echoes come through. */
function simTerminal(studioId: StudioId): string {
  return `TERM-${studioId.replace(/[^a-z]/gi, '').slice(0, 3).toUpperCase()}-1`;
}

/** yyyy-MM-dd `days` from `dayIso` (noon-UTC anchored, tz-independent). */
function shiftDay(dayIso: string, days: number): string {
  const d = new Date(`${dayIso.slice(0, 10)}T12:00:00Z`);
  return new Date(d.getTime() + days * 86_400_000).toISOString().slice(0, 10);
}

/** The next bank business day after `dayIso` — Fri, Sat and Sun all land Monday. */
export function nextBusinessDay(dayIso: string): string {
  let next = shiftDay(dayIso, 1);
  for (;;) {
    const dow = new Date(`${next}T12:00:00Z`).getUTCDay();
    if (dow !== 0 && dow !== 6) return next;
    next = shiftDay(next, 1);
  }
}

/** The deterministic id of a studio's simulated bank batch for a pay day. */
export function simLumpId(studioId: StudioId, payDayIso: string): string {
  return `${SIM_LUMP_PREFIX}${studioId}-${dayKey(payDayIso)}`;
}

/**
 * The terminal echoes a card/mixed sale AT the sale: one docket, for the card
 * portion only, named as the client. Cash and zero-card sales print nothing.
 * Idempotent per sale reference.
 */
export function echoTerminalDocket(sale: SalePayment): DocketLine | undefined {
  const amountAud = cardPortionAud(sale);
  if (amountAud <= 0) return undefined;
  const docket: DocketLine = {
    id: `${SIM_DOCKET_PREFIX}${sale.saleReference}`,
    studioId: sale.studioId,
    dayIso: dayKey(sale.takenAt),
    reference: sale.saleReference,
    amountAud,
    source: 'terminal',
    name: sale.customerName,
  };
  useBrainStore.getState().upsertDocketLine(docket);
  return docket;
}

/** Every reference any bank lump for the studio already carries. */
function settledRefs(studioId: StudioId): Set<string> {
  const refs = new Set<string>();
  for (const lump of Object.values(useBrainStore.getState().settlementLumps)) {
    if (lump.studioId !== studioId) continue;
    for (const l of lump.lines) refs.add(l.reference);
  }
  return refs;
}

/**
 * The bank batch for a studio's pay day, echoed AT day close. Takes the day's
 * terminal dockets no lump carries yet (plus the previous day's after-cutoff
 * ones, which ride the next batch by design) into `lump-sim-<studio>-<payDay>`, landing the
 * next business day, with lines that sum to the total. Idempotent: an existing
 * batch keeps its lines and landing day; only a docket new since the last close
 * is appended. Returns undefined (and writes nothing) when there is no batch.
 * `simOnly` restricts the batch to this simulator's own echoes (B1 seed history).
 */
export function echoSettlementBatch(
  studioId: StudioId,
  dayIso: string,
  opts: { simOnly?: boolean } = {},
): SettlementLump | undefined {
  const brain = useBrainStore.getState();
  const payDay = dayKey(dayIso);
  const id = simLumpId(studioId, payDay);
  const existing = brain.settlementLumps[id];
  const settled = settledRefs(studioId);
  const saleByRef = new Map(
    Object.values(brain.salePayments)
      .filter((s) => s.studioId === studioId)
      .map((s) => [s.saleReference, s]),
  );

  const fresh: SettlementLine[] = [];
  for (const d of Object.values(brain.docketLines)) {
    if (d.studioId !== studioId || d.source !== 'terminal' || settled.has(d.reference)) continue;
    // `simOnly`: just this simulator's own echoes — seeded reconciliation stories
    // (their own docket ids) keep their deliberate unsettled / mismatched state.
    if (opts.simOnly && !d.id.startsWith(SIM_DOCKET_PREFIX)) continue;
    const sale = saleByRef.get(d.reference);
    const day = dayKey(d.dayIso);
    // An after-cutoff sale belongs to the NEXT day's batch (and only that one).
    if ((sale?.afterCutoff ? shiftDay(day, 1) : day) !== payDay) continue;
    fresh.push({
      reference: d.reference,
      amountAud: d.amountAud,
      terminal: simTerminal(studioId),
      paidAt: sale?.takenAt ?? `${day}T12:00:00+10:00`,
    });
  }
  if (fresh.length === 0) return existing;

  fresh.sort((a, b) => a.paidAt.localeCompare(b.paidAt) || a.reference.localeCompare(b.reference));
  const lines = [...(existing?.lines ?? []), ...fresh];
  const lump: SettlementLump = {
    id,
    studioId,
    landedOn: existing?.landedOn ?? nextBusinessDay(payDay),
    amountAud: lines.reduce((sum, l) => sum + l.amountAud, 0),
    lines,
  };
  brain.upsertSettlementLump(lump);
  return lump;
}

/**
 * Console "Land pending batches": echo TODAY's bank batch from the simulated
 * terminal echoes still unsettled (without closing the day), then land every
 * simulated batch due after today, today — the demo clock is frozen, so the next
 * business day never comes on its own. Earlier days are left as they are (their
 * seeded overdue / missing-docket stories stay intact); seeded lumps are never
 * touched. Returns how many batches it echoed or landed.
 */
export function landPendingBatches(studioId: StudioId): number {
  const today = dayKey(useSimulationStore.getState().clockIso);
  const brain = useBrainStore.getState();
  const settled = settledRefs(studioId);
  const pendingToday = Object.values(brain.docketLines).some(
    (d) =>
      d.studioId === studioId &&
      d.id.startsWith(SIM_DOCKET_PREFIX) &&
      !settled.has(d.reference) &&
      dayKey(d.dayIso) === today,
  );
  const touched = new Set<string>();
  if (pendingToday) {
    const lump = echoSettlementBatch(studioId, today);
    if (lump) touched.add(lump.id);
  }

  for (const lump of Object.values(useBrainStore.getState().settlementLumps)) {
    if (lump.studioId !== studioId || !lump.id.startsWith(SIM_LUMP_PREFIX)) continue;
    if (lump.landedOn <= today) continue;
    useBrainStore.getState().upsertSettlementLump({ ...lump, landedOn: today });
    touched.add(lump.id);
  }
  return touched.size;
}
